import { useState, useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'hooks/redux'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'

import {
  CHECPLUS_MODULE_TYPES,
  NEW,
  VEHICLE_SUBTYPES,
  INITIAL,
} from 'utils/constants'
import strings from 'utils/strings'
import { isValidFeatureMetaData } from 'utils/feature'

import {
  setFeatureField,
  resetCurrentFeatureMetadata,
} from 'actions/feature/featureMetadata'
import {
  resetPreCreateNewFeature,
  createFeature,
  setIsPreCreateFeature,
} from 'actions/feature/features'

import { getSubGroups } from 'selectors/feature/inputs'
import {
  getFeatureMetadata,
  getFeatureIsValid,
} from 'selectors/feature/featureMetadata'
import { getFunctionLinks } from 'selectors/feature/functions'
import { getIsSameOrgNetwork } from 'selectors/user'
import {
  getValidationDescriptions,
  getInitialFeature,
} from 'selectors/feature/features'
import {
  getFeatureHarnessIds,
  getShowHarnesses,
} from 'selectors/feature/harnesses'
import { hasRight } from 'selectors/authorization'
import { CREATE_FEATURE } from 'constants/authorization'
import { INTERNAL } from 'constants/featureTypes'

export default function useFeaturePreCreateModalHook() {
  const dispatch = useDispatch()
  const history = useHistory()
  const [busMode, setBusModeSwitch] = useState(false)
  // API error state — when set, the modal body is replaced with ModalErrorContent
  const [modalError, setModalError] = useState(null)

  const featureMetadata = useSelector(getFeatureMetadata)
  const subGroups = useSelector(getSubGroups)
  const functionLinkScraps = useSelector(getFunctionLinks)

  const { moduleType, status, orgId, type, id: featureId } = featureMetadata

  const isSameOrg = useSelector(getIsSameOrgNetwork, { orgId })
  const moduleNameIsEditable =
    status === NEW && moduleType === CHECPLUS_MODULE_TYPES.XMC
  const canCreateInternalFeature = useSelector(
    hasRight,
    CREATE_FEATURE.INTERNAL
  )
  const isInternalFeature = type === INTERNAL
  const allDescriptions = useSelector(getValidationDescriptions)
  const featureIsValid = useSelector(getFeatureIsValid)
  const initialFeature = useSelector(getInitialFeature)
  const harnesses = useSelector(getFeatureHarnessIds)
  const showHarnesses = useSelector(getShowHarnesses)

  const setFeatureFieldFromEvent = (fieldName) => (event, id) =>
    dispatch(setFeatureField(fieldName, event.target.value, id))
  const setFeatureFieldFromValue = (fieldName) => (value, id) =>
    dispatch(setFeatureField(fieldName, value, id))

  const updateDescription = setFeatureFieldFromValue('description')
  const updateMasterNumber = setFeatureFieldFromEvent('masterNumber')
  const updateReleaseNumber = setFeatureFieldFromValue('releaseNumber')
  const updateFeatureType = setFeatureFieldFromEvent('type')
  const updateSubGroup = setFeatureFieldFromValue('subGroup')
  const updateSuccessorCode = setFeatureFieldFromValue('successorCode')
  const updatePriority = setFeatureFieldFromValue('priority')
  const setBusTypeFeature = setFeatureFieldFromValue('vehicleSubType')

  const isInitial = featureId === INITIAL
  const {
    type: initialFeatureType,
    description: initialDescription,
    priority,
    releaseNumber,
    subGroup,
  } = initialFeature || {}
  // validate feature form before enabling save
  const isValidNewFeature =
    isInitial &&
    isValidFeatureMetaData(
      initialFeatureType,
      priority,
      subGroup,
      releaseNumber,
      initialDescription
    )

  useEffect(() => {
    // set precreate mode to control feature metadata editability
    dispatch(setIsPreCreateFeature())
    return () => dispatch(setIsPreCreateFeature())
  }, [dispatch])

  const handleCancelCreate = useCallback(() => {
    resetCurrentFeatureMetadata(featureId)
    dispatch(resetPreCreateNewFeature())
  }, [dispatch, featureId])

  // Exposed so the component can clear the error state when closing the modal,
  // preventing it from showing the error body immediately on the next open.
  const clearModalError = useCallback(() => setModalError(null), [])

  const handleCreate = useCallback(
    (callback) => {
      setModalError(null)
      const redirect = ({ currentFeature }) => {
        callback()
        history.push({
          pathname: `/features/${Object.values(currentFeature.feature)[0].id}`,
          search: '?editing=true',
          state: {
            isNewFeature: true,
          },
        })
      }
      dispatch(createFeature(redirect, setModalError))
    },
    [dispatch, history]
  )

  const harnessesMapped = useMemo(
    () => harnesses.map(strings.harness_display_in_list),
    [harnesses]
  )

  const onBusModeChange = useCallback(
    (switchState) => {
      setBusModeSwitch(switchState)
      const vehicleType = switchState ? VEHICLE_SUBTYPES.BUS : null
      setBusTypeFeature(vehicleType, featureId)
    },
    [featureId, setBusTypeFeature]
  )

  return {
    ...featureMetadata,
    busMode,
    isSameOrg,
    moduleNameIsEditable,
    subGroups,
    functionLinkScraps,
    isInternalFeature,
    initialFeature,
    isValidNewFeature,
    isInitial,
    featureId,
    featureIsValid,
    allDescriptions,
    canCreateInternalFeature,
    showHarnesses,
    modalError,
    clearModalError,
    updateDescription,
    updateMasterNumber,
    updateReleaseNumber,
    updateFeatureType,
    updateSubGroup,
    updateSuccessorCode,
    updatePriority,
    handleCancelCreate,
    harnessesMapped,
    handleCreate,
    onBusModeChange,
  }
}
